import { Bar, Rectangle, type RectangleProps } from "recharts";
import { Chart, ChartTypes } from "@/uiCore";
import CostChartTooltip from "../../summary/components/CostChartTooltip";
import { EmptyScreen } from "../misc";
import { useAppState } from "@/modules/app/useAppContext";
import { CostGraphEntry, GetWarehouseSummaryCostGraphParams } from "../types";

interface CostsGraphProps {
  stackedData: CostGraphEntry[];
  aggregation: GetWarehouseSummaryCostGraphParams["aggregation"];
}

const CostsGraph = ({ stackedData, aggregation }: CostsGraphProps) => {
  const { currencySymbol } = useAppState();

  if (stackedData && stackedData.length === 0) {
    return (
      <EmptyScreen
        title="Graph not available"
        subTitle="No data available for the selected period."
      />
    );
  }

  return (
    <div style={{ height: "400px", width: "100%" }}>
      <Chart
        title="Cost"
        xAxisDataKey="time"
        yAxisLabel={{
          value: "",
          angle: 0,
          fill: "black",
          position: "insideTopLeft",
          offset: 5,
        }}
        yAxisLabelFormatter={value => `${currencySymbol}${value}`}
        type={ChartTypes.BarChart}
        data={stackedData}
        tooltipProps={{
          content: props => (
            <CostChartTooltip
              {...props}
              timeRange={aggregation as "day" | "week" | "month"}
              isFuture={false}
              type={"warehouse"}
            />
          ),
        }}
      >
        {({ barSize, selectedLegend }) => (
          <>
            {/* <ReferenceLine
              y={threshold}
              stroke="#B8BFCA"
              strokeDasharray="3 3"
              label={{
                position: "left",
                offset: -60,
                value: `${currencySymbol}${threshold}`,
                fill: "#66768D",
              }}
            /> */}

            <Bar
              barSize={barSize}
              hide={Boolean(selectedLegend && selectedLegend !== "compute cost")}
              name="Compute cost"
              dataKey="compute cost"
              fill="#5B9FFF"
              stackId="cost"
              shape={(rawProps: unknown) => {
                const props = rawProps as RectangleProps & { payload?: Record<string, number> };
                if (
                  props.payload?.["query acceleration cost"] === 0 &&
                  props.payload?.["cloud services cost"] === 0
                ) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              }}
            />
            <Bar
              barSize={barSize}
              hide={Boolean(selectedLegend && selectedLegend !== "cloud services cost")}
              name="Cloud services cost"
              dataKey="cloud services cost"
              fill="#FF754C"
              stackId="cost"
              shape={(rawProps: unknown) => {
                const props = rawProps as RectangleProps & { payload?: Record<string, number> };
                if (props.payload?.["query acceleration cost"] === 0) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              }}
            />
            <Bar
              barSize={barSize}
              dataKey="query acceleration cost"
              hide={Boolean(selectedLegend && selectedLegend !== "query acceleration cost")}
              name="Query acceleration cost"
              fill="#FFCE73"
              stackId="cost"
              radius={[5, 5, 0, 0]}
            />
          </>
        )}
      </Chart>
    </div>
  );
};

export default CostsGraph;
